import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Gift, HeartHandshake, Wine, Users, ArrowDown } from 'lucide-react';

const CustomerFeatures = () => {
  const features = [
    {
      icon: HeartHandshake,
      title: "AI Recommendations",
      description: "Our AI learns your unique tastes and preferences. Creates personalized suggestions just for you. Gets smarter with every order!",
      impact: "94% satisfaction rate",
      color: "text-emerald-400"
    },
    {
      icon: Gift,
      title: "Allergy-Safe Dining",
      description: "Set dietary restrictions once, we'll always remember. Every suggestion guaranteed safe for your needs. Dine with complete confidence!",
      impact: "100% allergy safe",
      color: "text-blue-400"
    },
    {
      icon: Wine,
      title: "Perfect Drink Pairings",
      description: "Expertly matched beverage recommendations for every meal. From wine to cocktails, we know what works. Elevate your dining experience!",
      impact: "AI-curated pairings",
      color: "text-purple-400"
    },
    {
      icon: DollarSign,
      title: "Smart Budget Control",
      description: "Stay within your price range, whether splurging or saving. Quality recommendations that respect your budget. Smart spending made simple!",
      impact: "Stay within budget",
      color: "text-dineai-yellow"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section id="customer-features" className="py-12 bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
            <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
              <Users className="w-4 h-4" />
              FOR FOOD LOVERS
            </span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
            Food Discovery <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">Reimagined</span> For You
          </h2>
          
          <p className="text-white/80 text-base max-w-2xl mx-auto">
            Never struggle with "what should I eat?" again. Our AI understands your preferences and finds perfect matches every time.
          </p>
        </motion.div>
            
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12">
          {features.map((feature, index) => (
            <motion.div 
              key={index}
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="group"
            >
              <div className="backdrop-blur-sm rounded-xl p-6 border border-yellow-400/30 shadow-xl hover:shadow-2xl hover:scale-105 transition-all h-full" style={{ backgroundColor: '#3256dc' }}>
                <div className="mb-4">
                  <feature.icon className={`w-10 h-10 ${feature.color}`} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-white/70 text-sm mb-4">{feature.description}</p>
                <div className="pt-4 border-t border-white/10">
                  <div className="text-dineai-yellow font-semibold text-sm">{feature.impact}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {/* CTA Section */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex items-center justify-center gap-3 text-white text-lg mb-2">
            <span className="font-medium">Find your perfect meal with AI-powered recommendations at participating restaurants</span>
          </div>
          <p className="text-white/70 mb-6">
            Download the app and discover your next favorite dish
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="https://apps.apple.com/us/app/dine-ai/id6743492809" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-110 hover:-translate-y-1">
              <div className="backdrop-blur-sm p-4 rounded-xl border border-yellow-400/30 shadow-xl group-hover:shadow-2xl transition-shadow duration-300" style={{ backgroundColor: '#3256dc' }}>
                <img
                  src="/apple-store.png"
                  alt="Download on the App Store"
                  className="h-14 w-auto"
                />
              </div>
            </a>

            <a href="https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-110 hover:-translate-y-1">
              <div className="backdrop-blur-sm p-4 rounded-xl border border-yellow-400/30 shadow-xl group-hover:shadow-2xl transition-shadow duration-300" style={{ backgroundColor: '#3256dc' }}>
                <img
                  src="/google-playstore.png"
                  alt="Get it on Google Play"
                  className="h-14 w-auto"
                />
              </div>
            </a>
          </div>
        </motion.div>
        
        {/* Bridge to next section */}
        <motion.div 
          className="mt-16 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div 
            className="relative group cursor-pointer"
            onClick={() => {
              const element = document.getElementById('testimonials');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-dineai-yellow/20 via-dineai-yellow/30 to-dineai-yellow/20 blur-xl group-hover:blur-2xl transition-all duration-300"></div>
            <div className="relative bg-gradient-to-r from-dineai-yellow/10 via-dineai-yellow/20 to-dineai-yellow/10 backdrop-blur-sm border border-dineai-yellow/30 rounded-2xl px-8 py-4 group-hover:border-dineai-yellow/50 transition-all duration-300">
              <p className="text-white/90 text-base font-medium flex items-center gap-3">
                <span className="bg-gradient-to-r from-white via-dineai-yellow to-white bg-clip-text text-transparent">
                  Hear from restaurants and diners who've transformed their experience with Dine AI...
                </span>
                <ArrowDown className="w-5 h-5 text-dineai-yellow animate-bounce" />
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CustomerFeatures;
